"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "@/components/theme/ThemeProvider";
import { cn } from "@/lib/utils";

interface ThemeToggleProps {
  className?: string;
  variant?: "default" | "programs-light" | "charity-dark";
}

export function ThemeToggle({
  className,
  variant = "default",
}: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();
  const reduced = useReducedMotion();
  const isLight = theme === "light";

  return (
    <motion.button
      type="button"
      onClick={toggleTheme}
      whileHover={reduced ? undefined : { rotate: 15 }}
      whileTap={{ scale: 0.92, rotate: 15 }}
      transition={{ duration: 0.15 }}
      aria-label={isLight ? "Switch to dark mode" : "Switch to light mode"}
      className={cn(
        "relative inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        variant === "charity-dark"
          ? "border border-white/35 text-white hover:bg-white/15"
          : variant === "programs-light"
            ? "border border-[var(--programs-green)]/20 text-[var(--programs-green-dark)] hover:bg-[var(--programs-green)]/10"
            : "border border-border text-foreground hover:bg-white/10",
        className
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={isLight ? "moon" : "sun"}
          initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
          transition={{ duration: 0.2 }}
          className="inline-flex"
        >
          {isLight ? <Moon size={18} /> : <Sun size={18} />}
        </motion.span>
      </AnimatePresence>
    </motion.button>
  );
}
